import stringFormatter from '@/utils/format/string';
import dateFormatter, { ONE_MINUTE_IN_SECONDS, ONE_SECOND_IN_MILLISECONDS } from '@/utils/format/date';
import VueI18n from 'vue-i18n';

export default class SystemDate extends Date {
  static createFromLocalTimestamp(timestamp: number) {
    return new SystemDate(
      SystemDate.getUTCTimestampFromLocalTimestamp(timestamp)
    );
  }

  static getUTCTimestampFromLocalTimestamp(timestamp: number): number {
    return timestamp + SystemDate.getTimezoneOffsetInMilliseconds();
  }

  static getTimezoneOffsetInMilliseconds(): number {
    const timezoneInMinutes: number = new Date().getTimezoneOffset();

    return timezoneInMinutes * ONE_MINUTE_IN_SECONDS * ONE_SECOND_IN_MILLISECONDS;
  }

  getRightMonth(): number {
    return this.getMonth() + 1;
  }

  getPaddedFullMonth(): string {
    return stringFormatter.t2(this.getRightMonth());
  }

  getHumanFullMonth(): string | VueI18n.TranslateResult {
    return dateFormatter.getHumanMonthByDate(this);
  }

  getHumanFullMonthTh(): string | VueI18n.TranslateResult {
    return dateFormatter.getHumanMonthThByDate(this);
  }

  getHumanFullWeek(): string | VueI18n.TranslateResult {
    return dateFormatter.getHumanFullWeekByDate(this);
  }

  getHumanShortWeek(): string | VueI18n.TranslateResult {
    return dateFormatter.getHumanShortWeekByDate(this);
  }

  getHumanTime() {
    return `${this.getHumanHours()}:${this.getHumanMinutes()}`;
  }

  getHumanDate(): string {
    return stringFormatter.t2(this.getDate());
  }

  getHumanHours(): string {
    return stringFormatter.t2(this.getHours());
  }

  getHumanMinutes(): string {
    return stringFormatter.t2(this.getMinutes());
  }

  getHumanSeconds(): string {
    return stringFormatter.t2(this.getMinutes());
  }

  isSameDays(date: Date): boolean {
    return this.getFullYear() === date.getFullYear() &&
      this.getMonth() === date.getMonth() &&
      this.getDate() === date.getDate();
  }

  getStartOfDay(): SystemDate {
    const newDate = new SystemDate(this.getTime());

    newDate.setHours(0, 0, 0, 0);

    return newDate;
  }

  getFullHash() {
    return `${this.getFullYear()}-${this.getPaddedFullMonth()}-${this.getHumanDate()}`;
  }

  getSmallHash() {
    return `${this.getFullYear()}-${this.getPaddedFullMonth()}`;
  }

  getLocalTime() {
    return this.getTime() - SystemDate.getTimezoneOffsetInMilliseconds();
  }

  public getCountOfDaysInTheYear(): number {
    return this.getFullYear() % 4 === 0 ? 366 : 365;
  }

  getEuropeanDay() {
    const day = this.getDay();

    return day === 0 ? 6 : day - 1;
  }

  getTodayTimeInSeconds() {
    return this.getLocalTime() % 86400000 / 1000;
  }

  getHumanDateString(): any {
    const now = (new SystemDate()).getStartOfDay().getTime();

    return dateFormatter.getHumanDateString(this, now);
  }

  getCountOfDaysInMonth(): any {
    const COUNT_OF_DAY_IN_MONTH = [
      31,
      0 === this.getFullYear() % 4 ? 29 : 28,
      31,
      30,
      31,
      30,
      31,
      31,
      30,
      31,
      30,
      31,
    ];

    return COUNT_OF_DAY_IN_MONTH[this.getMonth()];
  }

  clone() {
    return new SystemDate(this.getTime());
  }

  addDays(daysCount: number) {
    this.setDate(this.getDate() + daysCount);

    return this;
  }

  addYears(yearsCount: number) {
    this.setFullYear(this.getFullYear() + yearsCount);

    return this;
  }

  isSameOrLater(date: SystemDate): boolean {
    return this.getTime() >= date.getTime();
  }
}
